import Observer from '../../common/scripts/observer';

const observer = new Observer();
const ANIMATION_SPEED = 300;

class ScrollBy {
    constructor(options) {
        const defSpeedAnimation = 1000;
        const defaultOffset = 0;

        this.options = options;
        this.options.anchors = options.anchors || [];
        this.options.offset = options.offset || defaultOffset;
        this.options.animate = options.animate !== false;
        this.options.speedAnimation = options.speedAnimation || defSpeedAnimation;
        this.options.takeElements = options.takeElements || [];
        this.offset = this.options.offset;
        this.animationName = null;
    }

    init() {
        this._initAnchors();
        this._parseHashParams();
    }

    /**
     * Метод скроллит по клику на кнопку с указаными в дата атрибуте id элемента, к которому нужно проскролить
     * @param {object} element - дом-элемент с которого ловится клик
     * @public
     */
    scrollToHref(element) {
        element.addEventListener('click', (event) => {
            event.preventDefault();

            const id = element.dataset.scrollBy || element.getAttribute('href');
            const target = document.querySelector(id);

            this._setActiveClass(element);
            this._setOffsetTakeElements();
            this._scroll(target);
            this._setGetParams(id);
        });
    }

    /**
     * Метод скролит к дом элементу
     * @param {object} element - дом-элемент к которому необходимо проскролить
     * @public
     */
    scrollToElement(element) {
        this._scroll(element);
    }

    /**
     * Метод скролит к якорю из гет-параметра
     * @public
     */
    scrollToGetAnchor() {
        this._parseGetParams();
    }

    /**
     * Метод скролит наверх страницы
     * @public
     */
    scrollToTop() {
        this._scroll();
    }

    /**
     * Метод навешивает обработчики событий во время скролла
     * @private
     */
    _bindScrollEvents() {
        window.addEventListener('mousewheel', this._scrollStop.bind(this));

        window.addEventListener('touchstart', this._scrollStop.bind(this));
    }

    /**
     * Метод удаляет обработчики событий во время скролла
     * @private
     */
    _removeBindScrollEvents() {
        window.removeEventListener('mousewheel', this._scrollStop.bind(this));

        window.removeEventListener('touchstart', this._scrollStop.bind(this));
    }

    /**
     * Метод инициализирует якоря из массива.
     * @private
     */
    _initAnchors() {
        if (!this.options.anchors.length) {
            return;
        }

        this.options.anchors.forEach((item) => {
            this.scrollToHref(item);
        });
    }

    /**
     * Метод выполняет анимацию скрола
     * @param {object} element - дом-элемент к которому необходимо проскролить
     * @private
     */
    _scroll(element = false) {
        const x = 0;
        const y = element ? (element.getBoundingClientRect().top + pageYOffset) - this.offset : 0;

        observer.publish('scrollByStart');

        if (this.options.animate) {
            const duration = this.options.speedAnimation;
            const startY = window.pageYOffset;
            const diff = (element ? (element.getBoundingClientRect().top + pageYOffset) - this.offset : 0) -
                startY;
            let start = 0;

            const step = (timestamp) => {
                if (!start) {
                    start = timestamp;
                }

                const time = timestamp - start;
                const percent = Math.min(time / duration, 1);

                window.scrollTo(0, startY + (diff * percent));

                if (time < duration) {
                    this.animationName = window.requestAnimationFrame(step.bind(this));
                }
            };

            this.animationName = window.requestAnimationFrame(step.bind(this));

            setTimeout(() => {
                observer.publish('scrollByFinish');

                this._removeBindScrollEvents();
            }, this.options.speedAnimation);
        }

        if (!this.options.animate) {
            window.scrollTo(x, y);
        }

        this._bindScrollEvents();
    }

    /**
     * Метод останавливает скролл
     * @private
     */
    _scrollStop() {
        window.cancelAnimationFrame(this.animationName);
    }

    /**
     * Метод парсит гет-параметры
     * @private
     */
    _parseGetParams() {
        const get = window.location.search;

        if (get) {
            const url = new URLSearchParams(get);

            for (const item of url.entries()) {
                if (item[0] === 'anchor') {
                    const element = document.querySelector(`#${item[1]}`);

                    this._scroll(element);
                }
            }
        }
    }

    /**
     * Метод парсит hash-параметр
     * @private
     */
    _parseHashParams() {
        const hash = window.location.hash;
        const isScroll = Boolean(hash) && this.options.anchors.reduce((isSame, item) => {
            return isSame || item.hash === hash;
        }, false);

        if (isScroll) {
            const element = document.querySelector(`${hash}`);

            if (element) {
                document.addEventListener('readystatechange', () => {
                    if (document.readyState === 'complete') {
                        setTimeout(() => {
                            this._setOffsetTakeElements();
                            this._scroll(element);
                        }, ANIMATION_SPEED);
                    }
                });
            }
        }
    }

    /**
     * Устанавливает отступ в зависимости от высоты переданного элемента и дополнительной настройки
     * @private
     */
    _setOffsetTakeElements() {
        const takeElements = this.options.takeElements;

        if (takeElements.length) {
            let sum = 0;

            takeElements.forEach((item) => {
                if (item) {
                    sum = sum + item.offsetHeight;
                }
            });

            this.offset = -this.options.offset + sum;
        }
    }

    /**
     * Устанавливает активный класс для якоря
     * @param {object} element - дом-элемент по которому произошел клик
     * @private
     */
    _setActiveClass(element) {
        if (!this.options.anchors.length) {
            return;
        }

        this.options.anchors.forEach((item) => {
            item.classList.remove('is-active');
        });

        element.classList.add('is-active');
    }

    /**
     * Устанавливает id якоря как гет-параметр
     * @param {String} id - id якоря
     * @private
     */
    _setGetParams(id) {
        document.location.hash = id;
    }
}

export default ScrollBy;
